import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../lib/theme/index.js';
import SmartCampSelector from './SmartCampSelector';

interface AddToCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (campId: string, startDate: string, endDate: string) => Promise<void>;
  childId: string;
  childName: string;
  editingSchedule?: { scheduleId: string; campId: string; startDate: string; endDate: string } | null;
}

export default function AddToCalendarModal({
  isOpen,
  onClose,
  onAdd,
  childId,
  childName,
  editingSchedule
}: AddToCalendarModalProps) {
  const session = useSession();
  const { currentTheme } = useTheme();
  const [selectedCamp, setSelectedCamp] = useState<string>(editingSchedule?.campId || '');
  const [selectedCampName, setSelectedCampName] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(editingSchedule?.startDate || '');
  const [endDate, setEndDate] = useState<string>(editingSchedule?.endDate || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [endDateInputFocused, setEndDateInputFocused] = useState(false);
  const navigate = useNavigate();

  // Update form when editingSchedule changes
  useEffect(() => {
    const fetchCampName = async () => {
      if (editingSchedule?.campId) {
        try {
          const { data, error } = await supabase
            .from('camps')
            .select('name')
            .eq('id', editingSchedule.campId)
            .single();
          
          if (error) {
            console.error('Error fetching camp name:', error);
            return;
          }
          
          if (data) {
            setSelectedCampName(data.name);
          }
        } catch (err) {
          console.error('Failed to fetch camp name:', err);
        }
      }
    };

    if (editingSchedule) {
      setSelectedCamp(editingSchedule.campId);
      setStartDate(editingSchedule.startDate);
      setEndDate(editingSchedule.endDate);
      void fetchCampName();
    } else {
      setSelectedCamp('');
      setSelectedCampName('');
      setStartDate('');
      setEndDate('');
      setEndDateInputFocused(false);
    }
  }, [editingSchedule]);

  // Fetch children - use same query key as Calendar page to share cache
  const { data: children = [] } = useQuery(
    ['children', session?.user?.id], // Match Calendar page query key
    async () => {
      if (!session?.user?.id) return [];
      
      const { data, error } = await supabase
        .from('children')
        .select('id, user_id, name, date_of_birth, created_at, updated_at')
        .eq('user_id', session.user.id)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    {
      enabled: !!session?.user?.id, // Only fetch if user is authenticated
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCamp || !startDate || !endDate) return;

    try {
      setIsSubmitting(true);
      await onAdd(selectedCamp, startDate, endDate);
      // Reset form only if not editing (will be reset by parent)
      if (!editingSchedule) {
        setSelectedCamp('');
        setSelectedCampName('');
        setStartDate('');
        setEndDate('');
        setEndDateInputFocused(false);
      }
    } catch (error) {
      console.error('Error in AddToCalendarModal:', error);
      // Close modal on error so user can see the toast notification
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddChild = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    navigate('/kids-schedule');
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-[9999]" 
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div 
            className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity"
            onClick={(e) => e.stopPropagation()}
          />
        </Transition.Child>

        <div 
          className="fixed inset-0 z-10 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel 
                className="relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
                style={{
                  padding: '1px',
                  background: `linear-gradient(to right, ${currentTheme.primary}, ${currentTheme.secondary})`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white rounded-lg relative">
                  <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
                    <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                        <CalendarDaysIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          {editingSchedule ? 'Edit Schedule' : `Add ${childName} to Kids Schedule`}
                        </Dialog.Title>
                      </div>
                    </div>

                    <div className="mt-4 space-y-4">
                      {/* If childId is provided, we know a child exists - show the form */}
                      {/* Only check children.length if no childId was passed */}
                      {!childId && children.length === 0 ? (
                        <div className="text-center">
                          <p className="text-sm text-gray-500 mb-4">No children added yet</p>
                          <button
                            type="button"
                            onClick={handleAddChild}
                            className="btn btn-primary"
                          >
                            Add Child Profile
                          </button>
                        </div>
                      ) : (
                        <>
                          {!editingSchedule && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <label htmlFor="camp" className="block text-sm font-medium text-gray-700 mb-1">
                                Select Camp
                              </label>
                              <SmartCampSelector
                                value={selectedCamp}
                                onChange={(campId, campName) => {
                                  setSelectedCamp(campId);
                                  setSelectedCampName(campName);
                                }}
                                placeholder="Search for a camp..."
                                required
                              />
                            </div>
                          )}
                          {editingSchedule && (
                            <div className="rounded-md bg-gray-50 px-3 py-2 border border-gray-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Camp
                              </label>
                              <p className="text-sm text-gray-900">
                                {selectedCampName || 'Loading...'}
                              </p>
                            </div>
                          )}

                          <div className="flex space-x-4">
                            <div className="flex-1">
                              <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">
                                Start Date
                              </label>
                              <input
                                type="date"
                                id="start-date"
                                value={startDate}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setStartDate(e.target.value);
                                  // If end date is before new start date, clear it
                                  if (endDate && e.target.value && endDate < e.target.value) {
                                    setEndDate('');
                                  }
                                }}
                                min={(() => {
                                  const minDate = new Date();
                                  minDate.setDate(minDate.getDate() - 30); // Allow 30 days in the past
                                  return minDate.toISOString().split('T')[0];
                                })()}
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary date-picker-primary"
                                required
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>

                            <div className="flex-1">
                              <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">
                                End Date
                              </label>
                              <input
                                type="date"
                                id="end-date"
                                // When endDate is empty and input is focused, use startDate as temporary value
                                // so calendar opens to the same month. When user selects a date, it becomes the real value.
                                value={endDate || (endDateInputFocused && startDate ? startDate : '')}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const selectedDate = e.target.value;
                                  // Validate that end date is not before start date
                                  if (startDate && selectedDate && selectedDate < startDate) {
                                    // Don't allow selection of dates before start date
                                    return;
                                  }
                                  // Only set endDate if user actually selected a different date
                                  // If they selected the same as startDate, that's fine - it's a valid selection
                                  setEndDate(selectedDate);
                                  setEndDateInputFocused(false); // User made a selection, no longer need temp value
                                }}
                                onFocus={(e) => {
                                  e.stopPropagation();
                                  // Only use temp value if endDate is empty
                                  // This ensures the calendar opens to the start date month
                                  if (!endDate && startDate) {
                                    setEndDateInputFocused(true);
                                  }
                                }}
                                onBlur={(e) => {
                                  e.stopPropagation();
                                  setEndDateInputFocused(false);
                                  // If user didn't select a date (value is empty or same as startDate and endDate state is empty),
                                  // the input will show empty, which is correct
                                  // The form validation will catch if it's required
                                }}
                                min={startDate || (() => {
                                  const minDate = new Date();
                                  minDate.setDate(minDate.getDate() - 30); // Allow 30 days in the past
                                  return minDate.toISOString().split('T')[0];
                                })()}
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary date-picker-primary"
                                required
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Show submit buttons if childId is provided OR if children exist */}
                  {(childId || children.length > 0) && (
                    <div 
                      className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn btn-primary sm:ml-3 sm:w-auto"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isSubmitting ? (editingSchedule ? 'Updating...' : 'Adding...') : (editingSchedule ? 'Update Schedule' : 'Add to Calendar')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary mt-3 sm:mt-0 sm:w-auto"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}